import { useState, useEffect } from 'react'
import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Badge, Select, Modal, ModalHeader, ModalBody } from '../components/ui'
import { useCompany } from '../hooks/useCompany'
import { useAuth } from '../hooks/useAuth'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import { Icon, LatLngBounds } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { MapPin, Clock, User, CheckCircle } from 'lucide-react'

// Fix for default markers in react-leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

delete (Icon.Default.prototype as any)._getIconUrl
Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
})

// Custom marker icons for cleaner look
const createCustomIcon = (color: string) => new Icon({
  iconUrl: `data:image/svg+xml;base64,${btoa(`
    <svg width="25" height="41" viewBox="0 0 25 41" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.5 0C5.5 0 0 5.5 0 12.5C0 24.5 12.5 41 12.5 41C12.5 41 25 24.5 25 12.5C25 5.5 19.5 0 12.5 0Z" fill="${color}"/>
      <circle cx="12.5" cy="12.5" r="5" fill="white"/>
    </svg>
  `)}`,
  iconSize: [25, 41],
  iconAnchor: [12.5, 41],
  popupAnchor: [0, -41],
  shadowUrl: markerShadow,
  shadowSize: [41, 41],
  shadowAnchor: [13, 41]
})

const clockInIcon = createCustomIcon('#10b981') // Green
const clockOutIcon = createCustomIcon('#ef4444') // Red

// Component to handle map resizing and bounds
function MapResizer({ positions }: { positions: [number, number][] }) {
  const map = useMap()
  
  useEffect(() => {
    // Invalidate size to fix rendering issues
    setTimeout(() => {
      map.invalidateSize()
      
      // Fit bounds to show all markers
      if (positions.length > 1) {
        const bounds = new LatLngBounds(positions)
        map.fitBounds(bounds, { padding: [50, 50] })
      }
    }, 100)
  }, [map, positions])
  
  return null
}

interface TimesheetEntry {
  id: string
  user_id: string
  user_name: string
  user_email: string
  team_id: string
  location_id: string | null
  location_name: string
  clock_in_at: string
  clock_out_at: string | null
  method_in: string
  method_out: string | null
  total_hours: number | null
  total_break_minutes?: number
  status: 'pending' | 'approved'
  approved_at: number | null
  approved_by: string | null
  created_at: number
  updated_at: number
  // Location data
  clock_in_latitude?: number
  clock_in_longitude?: number
  clock_out_latitude?: number
  clock_out_longitude?: number
  location_latitude?: number
  location_longitude?: number
  location_radius?: number
}

interface Location {
  id: string
  name: string
}

export default function Timesheets() {
  const { currentCompany } = useCompany()
  const { } = useAuth()
  const [timesheets, setTimesheets] = useState<TimesheetEntry[]>([])
  const [locations, setLocations] = useState<Location[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState<string | null>(null)
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null)
  const [selectedEntry, setSelectedEntry] = useState<TimesheetEntry | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [filters, setFilters] = useState({
    location_id: '',
    status: '',
    time_period: 'this_week'
  })
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  })

  // Calculate date ranges based on time period
  const getDateRange = () => {
    const now = new Date()
    let startDate: string, endDate: string
    
    switch (filters.time_period) {
      case 'this_week':
        const startOfWeek = new Date(now)
        startOfWeek.setDate(now.getDate() - now.getDay())
        startDate = startOfWeek.toISOString().split('T')[0]
        endDate = now.toISOString().split('T')[0]
        break
      case 'last_week':
        const lastWeekStart = new Date(now)
        lastWeekStart.setDate(now.getDate() - now.getDay() - 7)
        const lastWeekEnd = new Date(lastWeekStart)
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6)
        startDate = lastWeekStart.toISOString().split('T')[0]
        endDate = lastWeekEnd.toISOString().split('T')[0]
        break
      case 'this_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]
        endDate = now.toISOString().split('T')[0]
        break
      case 'last_month':
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0)
        startDate = lastMonth.toISOString().split('T')[0]
        endDate = lastMonthEnd.toISOString().split('T')[0]
        break
      default:
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        endDate = now.toISOString().split('T')[0]
    }
    
    return { startDate, endDate }
  }

  // Fetch teams for current company
  useEffect(() => {
    const fetchTeamsAndSetCurrent = async () => {
      if (!currentCompany?.id) {
        setCurrentTeamId(null)
        setLoading(false)
        return
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/v1/teams?company_id=${currentCompany.id}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            }
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch teams')
        }

        const data = await response.json()
        const teams = data.teams || []
        
        // Find a team where the user is a member
        const userTeam = teams.find((team: any) => team.user_role !== null)
        
        if (userTeam) {
          setCurrentTeamId(userTeam.id)
        } else if (teams.length > 0) {
          setCurrentTeamId(teams[0].id)
        } else {
          setCurrentTeamId(null)
          setError('No teams found in this company')
        }
      } catch (err) {
        console.error('Failed to fetch teams:', err)
        setCurrentTeamId(null)
        setError('Failed to load teams')
      }
    }

    fetchTeamsAndSetCurrent()
  }, [currentCompany?.id])

  // Fetch timesheets
  useEffect(() => {
    if (!currentTeamId) {
      return
    }

    fetchTimesheets()
    fetchLocations()
  }, [currentTeamId, filters, pagination.page])

  const fetchTimesheets = async () => {
    if (!currentTeamId) return

    try {
      setLoading(true)
      setError(null)

      const { startDate, endDate } = getDateRange()
      const params = new URLSearchParams({
        team_id: currentTeamId,
        start_date: startDate,
        end_date: endDate,
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      })

      if (filters.location_id) {
        params.append('location_id', filters.location_id)
      }

      if (filters.status) {
        params.append('status', filters.status)
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/timesheets?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )

      if (!response.ok) {
        throw new Error('Failed to fetch timesheets')
      }

      const data = await response.json()
      setTimesheets(data.timesheets || [])
      setPagination(prev => ({
        ...prev,
        total: data.pagination.total,
        totalPages: data.pagination.totalPages
      }))
    } catch (err) {
      console.error('Failed to fetch timesheets:', err)
      setError(err instanceof Error ? err.message : 'Failed to load timesheets')
    } finally {
      setLoading(false)
    }
  }

  const fetchLocations = async () => {
    if (!currentTeamId) return

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/locations?team_id=${currentTeamId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )

      if (response.ok) {
        const data = await response.json()
        setLocations(data.locations || [])
      }
    } catch (err) {
      console.error('Failed to fetch locations:', err)
    }
  }

  const handleApprove = async (entryId: string) => {
    try {
      setSubmitting(entryId)
      setError(null)

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/timesheets/${entryId}/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )

      if (!response.ok) {
        throw new Error('Failed to approve timesheet')
      }

      // Refresh timesheets
      await fetchTimesheets()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve timesheet')
    } finally {
      setSubmitting(null)
    }
  }

  const handleViewDetails = async (entry: TimesheetEntry) => {
    setSelectedEntry(entry)
    setShowDetailModal(true)
    
    // Fetch detailed data with location info if needed
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/timesheets/${entry.id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )
      
      if (response.ok) {
        const data = await response.json()
        if (data.timesheet) {
          setSelectedEntry(data.timesheet)
        }
      }
    } catch (err) {
      console.error('Failed to fetch timesheet details:', err)
    }
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="bg-white shadow rounded-lg">
            <div className="h-12 bg-gray-200 rounded-t-lg mb-2"></div>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 mb-1"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load timesheets</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchTimesheets}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (!currentCompany) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <p className="text-gray-600">Please select a company to view timesheets.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 sm:px-0">
        <div className="sm:flex sm:items-center sm:justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">Timesheets</h1>
          <div className="mt-4 sm:mt-0 flex gap-3">
            <Select 
              className="w-40"
              value={filters.location_id}
              onChange={(e) => setFilters(prev => ({ ...prev, location_id: e.target.value }))}
            >
              <option value="">All Locations</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </Select>
            <Select 
              className="w-40"
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
            </Select>
            <Select 
              className="w-40"
              value={filters.time_period}
              onChange={(e) => setFilters(prev => ({ ...prev, time_period: e.target.value }))}
            >
              <option value="this_week">This Week</option>
              <option value="last_week">Last Week</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
            </Select>
            <Button variant="secondary">Export</Button>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Employee</TableHead>
                <TableHead>Clock In</TableHead>
                <TableHead>Clock Out</TableHead>
                <TableHead>Total Hours</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {timesheets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <div className="text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <p className="text-lg font-medium mb-1">No timesheets found</p>
                      <p className="text-sm">No timesheet entries match your current filters.</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                timesheets.map((timesheet) => (
                  <TableRow key={timesheet.id}>
                    <TableCell className="font-medium">{formatDate(timesheet.clock_in_at)}</TableCell>
                    <TableCell>{timesheet.user_name}</TableCell>
                    <TableCell>{formatTime(timesheet.clock_in_at)}</TableCell>
                    <TableCell>
                      {timesheet.clock_out_at ? formatTime(timesheet.clock_out_at) : (
                        <span className="text-green-600 font-medium">Currently clocked in</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        {timesheet.total_hours ? `${timesheet.total_hours}h` : '-'}
                        {timesheet.total_break_minutes && timesheet.total_break_minutes > 0 && (
                          <div className="text-xs text-gray-500">
                            ({timesheet.total_break_minutes} min break)
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{timesheet.location_name}</TableCell>
                    <TableCell>
                      <Badge
                        variant={timesheet.status === 'approved' ? 'success' : 'warning'}
                      >
                        {timesheet.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                           
                          variant="ghost"
                          onClick={() => handleViewDetails(timesheet)}
                        >
                          View
                        </Button>
                        {timesheet.status === 'pending' && (
                          <Button 
                             
                            variant="primary"
                            onClick={() => handleApprove(timesheet.id)}
                            disabled={submitting === timesheet.id}
                          >
                            {submitting === timesheet.id ? 'Approving...' : 'Approve'}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex gap-2">
              <Button 
                variant="secondary" 
                
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page <= 1}
              >
                Previous
              </Button>
              <span className="px-3 py-2 text-sm">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <Button 
                variant="secondary" 
                
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false)
          setSelectedEntry(null)
        }}
        size="lg"
      >
        <ModalHeader>Timesheet Entry Details</ModalHeader>
        <ModalBody>
        {selectedEntry && (
          <div className="space-y-6">
            {/* Employee Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <User className="w-5 h-5 text-gray-400 mr-2" />
                <h3 className="font-medium text-gray-900">Employee Information</h3>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Name:</span>
                  <p className="font-medium">{selectedEntry.user_name}</p>
                </div>
                <div>
                  <span className="text-gray-500">Email:</span>
                  <p className="font-medium">{selectedEntry.user_email}</p>
                </div>
              </div>
            </div>

            {/* Time Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <Clock className="w-5 h-5 text-gray-400 mr-2" />
                <h3 className="font-medium text-gray-900">Time Information</h3>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Clock In:</span>
                  <p className="font-medium">
                    {formatDate(selectedEntry.clock_in_at)}<br />
                    {formatTime(selectedEntry.clock_in_at)}
                  </p>
                  <Badge className="mt-1">{selectedEntry.method_in}</Badge>
                </div>
                <div>
                  <span className="text-gray-500">Clock Out:</span>
                  <p className="font-medium">
                    {selectedEntry.clock_out_at ? (
                      <>
                        {formatDate(selectedEntry.clock_out_at)}<br />
                        {formatTime(selectedEntry.clock_out_at)}
                      </>
                    ) : (
                      'Still clocked in'
                    )}
                  </p>
                  {selectedEntry.method_out && (
                    <Badge className="mt-1">{selectedEntry.method_out}</Badge>
                  )}
                </div>
              </div>
              {selectedEntry.total_hours && (
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                  <div>
                    <span className="text-gray-500">Total Hours:</span>
                    <p className="font-medium text-lg">{selectedEntry.total_hours.toFixed(2)} hours</p>
                  </div>
                  {selectedEntry.total_break_minutes && selectedEntry.total_break_minutes > 0 && (
                    <div>
                      <span className="text-gray-500">Break Duration:</span>
                      <p className="font-medium">{selectedEntry.total_break_minutes} minutes</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Location Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <MapPin className="w-5 h-5 text-gray-400 mr-2" />
                <h3 className="font-medium text-gray-900">Location Information</h3>
              </div>
              <div className="text-sm">
                <span className="text-gray-500">Location:</span>
                <p className="font-medium">{selectedEntry.location_name}</p>
              </div>
              
              {/* Map */}
              {(selectedEntry.clock_in_latitude && selectedEntry.clock_in_longitude) && (
                <div className="mt-4">
                  <div className="h-96 rounded-lg overflow-hidden border border-gray-200 relative">
                    <MapContainer
                      center={[selectedEntry.clock_in_latitude, selectedEntry.clock_in_longitude]}
                      zoom={15}
                      style={{ height: '100%', width: '100%' }}
                      scrollWheelZoom={false}
                    >
                      {/* Use CartoDB Positron for a clean, minimal aesthetic similar to Google Maps */}
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                        url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                      />
                      
                      {/* Map resizer to fix rendering issues */}
                      <MapResizer 
                        positions={[
                          [selectedEntry.clock_in_latitude, selectedEntry.clock_in_longitude],
                          ...(selectedEntry.clock_out_latitude && selectedEntry.clock_out_longitude 
                            ? [[selectedEntry.clock_out_latitude, selectedEntry.clock_out_longitude]] 
                            : [])
                        ].filter(Boolean) as [number, number][]}
                      />
                      
                      {/* Clock In Marker */}
                      <Marker 
                        position={[selectedEntry.clock_in_latitude, selectedEntry.clock_in_longitude]}
                        icon={clockInIcon}
                      >
                        <Popup>
                          <div className="text-sm">
                            <div className="font-semibold text-green-600 mb-1">Clock In</div>
                            <div className="text-gray-700">{formatTime(selectedEntry.clock_in_at)}</div>
                            <div className="text-gray-500 text-xs mt-1">{formatDate(selectedEntry.clock_in_at)}</div>
                          </div>
                        </Popup>
                      </Marker>
                      
                      {/* Clock Out Marker */}
                      {selectedEntry.clock_out_latitude && selectedEntry.clock_out_longitude && (
                        <Marker 
                          position={[selectedEntry.clock_out_latitude, selectedEntry.clock_out_longitude]}
                          icon={clockOutIcon}
                        >
                          <Popup>
                            <div className="text-sm">
                              <div className="font-semibold text-red-600 mb-1">Clock Out</div>
                              <div className="text-gray-700">{formatTime(selectedEntry.clock_out_at!)}</div>
                              <div className="text-gray-500 text-xs mt-1">{formatDate(selectedEntry.clock_out_at!)}</div>
                            </div>
                          </Popup>
                        </Marker>
                      )}
                    </MapContainer>
                  </div>
                  <div className="mt-2 text-xs text-gray-500 space-y-1">
                    {selectedEntry.clock_in_latitude && (
                      <p>Clock In Location: {selectedEntry.clock_in_latitude.toFixed(6)}, {selectedEntry.clock_in_longitude?.toFixed(6)}</p>
                    )}
                    {selectedEntry.clock_out_latitude && (
                      <p>Clock Out Location: {selectedEntry.clock_out_latitude.toFixed(6)}, {selectedEntry.clock_out_longitude?.toFixed(6)}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Status Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <CheckCircle className="w-5 h-5 text-gray-400 mr-2" />
                <h3 className="font-medium text-gray-900">Status Information</h3>
              </div>
              <div className="text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Status:</span>
                  <Badge variant={selectedEntry.status === 'approved' ? 'success' : 'warning'}>
                    {selectedEntry.status}
                  </Badge>
                </div>
                {selectedEntry.approved_at && selectedEntry.approved_by && (
                  <div className="mt-2 text-xs text-gray-500">
                    Approved on {formatDate(new Date(selectedEntry.approved_at * 1000).toISOString())} 
                    {' '}at {formatTime(new Date(selectedEntry.approved_at * 1000).toISOString())}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        </ModalBody>
      </Modal>
    </div>
  )
}